import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import StatCard from "../components/StatCard";
import Charts from "../components/Charts";
import StudentTable from "../components/StudentTable";
import FeeStructureManager from "../components/FeeStructureManager";
import AttendanceService from "../services/AttendanceService";
import StaffAttendanceService from "../services/StaffAttendanceService";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import {
  buildAccountsAnalytics,
  createEmptyAccountsAnalytics,
  formatInr,
} from "../utils/accountsAnalytics";

import {
  FaWallet,
  FaRupeeSign,
  FaMoneyBillWave,
  FaChartLine,
  FaUserTie,
  FaChalkboardTeacher,
  FaUserCheck,
  FaBus,
  FaUserGraduate,
} from "react-icons/fa";

export default function Dashboard() {
  const [activeMenu, setActiveMenu] = useState("Dashboard");
  const [attendance, setAttendance] = useState(null);
  const [staffSummary, setStaffSummary] = useState(null);
  const [accountsStats, setAccountsStats] = useState(
    createEmptyAccountsAnalytics(),
  );
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  function getStudentAttendanceDateKey() {
    const date = new Date();
    return date.toLocaleDateString("en-GB");
  }

  useEffect(() => {
    let isMounted = true;

    async function loadAttendance() {
      setLoading(true);
      setAccountsLoading(true);

      const [studentsResult, staffResult, accountsResult] =
        await Promise.allSettled([
          AttendanceService.getAttendanceByDate(getStudentAttendanceDateKey()),
          StaffAttendanceService.getTodayAttendanceData(),
          getDocs(collection(db, "received_payment")),
        ]);

      if (!isMounted) return;

      if (studentsResult.status === "fulfilled") {
        setAttendance(studentsResult.value);
      } else {
        console.error(
          "Dashboard student attendance error:",
          studentsResult.reason,
        );
      }

      if (staffResult.status === "fulfilled") {
        setStaffSummary(staffResult.value.summary);
      } else {
        console.error("Dashboard staff attendance error:", staffResult.reason);
      }

      if (accountsResult.status === "fulfilled") {
        const paymentRecords = accountsResult.value.docs.map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        }));
        setAccountsStats(buildAccountsAnalytics(paymentRecords));
      } else {
        console.error(
          "Dashboard accounts analytics error:",
          accountsResult.reason,
        );
        setAccountsStats(createEmptyAccountsAnalytics());
      }

      setLoading(false);
      setAccountsLoading(false);
    }

    loadAttendance();

    return () => {
      isMounted = false;
    };
  }, []);

  function toCountText(presentCount, totalCount) {
    return `${presentCount} / ${totalCount}`;
  }

  function getRoleValue(roleName) {
    if (loading || !staffSummary) {
      return "Loading...";
    }

    const roleCounts = StaffAttendanceService.getRoleCounts(
      staffSummary,
      roleName,
    );

    return toCountText(roleCounts.presentCount, roleCounts.totalCount);
  }

  const collectionRatioText = `${accountsStats.collectionRatio.toFixed(1)}%`;
  const outstandingText = accountsLoading
    ? "Loading..."
    : `${formatInr(accountsStats.totalOutstanding)} (${accountsStats.pendingStudents})`;
  const monthlyCollectionText = accountsLoading
    ? "Loading..."
    : formatInr(accountsStats.currentMonthCollection);
  const totalCollectedText = accountsLoading
    ? "Loading..."
    : formatInr(accountsStats.totalCollectedAllTime);
  const collectionRatioCardText = accountsLoading
    ? "Loading..."
    : collectionRatioText;

  const renderContent = () => {
    switch (activeMenu) {
      case "Student Information":
        return <div className="text-xl">Student Information Content</div>;

      case "Accounts":
        return <StudentTable />;

      case "Examinations":
        return <div className="text-xl">Examinations Content</div>;

      case "Fee Structure":
        return <FeeStructureManager />;

      default:
        return (
          <>
            <main className="flex-1 p-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Fee Awaiting Payment"
                  value={outstandingText}
                  icon={FaWallet}
                  iconBg="bg-purple-400"
                />

                <StatCard
                  title="Monthly Fee Collection"
                  value={monthlyCollectionText}
                  icon={FaRupeeSign}
                  iconBg="bg-green-400"
                />

                <StatCard
                  title="Total Collected Till Date"
                  value={totalCollectedText}
                  icon={FaMoneyBillWave}
                  iconBg="bg-red-400"
                />

                <StatCard
                  title="Collection Ratio"
                  value={collectionRatioCardText}
                  icon={FaChartLine}
                  iconBg="bg-yellow-400"
                />

                <StatCard
                  title="Teachers Present"
                  value={getRoleValue("teacher")}
                  icon={FaUserTie}
                  iconBg="bg-pink-400"
                  x
                  onClick={() => navigate("/staff-attendance?preset=teacher")}
                />

                <StatCard
                  title="Class Teachers Present"
                  value={getRoleValue("class teacher")}
                  icon={FaChalkboardTeacher}
                  iconBg="bg-blue-500"
                  onClick={() =>
                    navigate("/staff-attendance?preset=class-teacher")
                  }
                />

                <StatCard
                  title="Attenders Present"
                  value={getRoleValue("attender")}
                  icon={FaUserCheck}
                  iconBg="bg-rose-400"
                  onClick={() => navigate("/staff-attendance?preset=attender")}
                />

                <StatCard
                  title="Students Present Today"
                  value={
                    loading || !attendance
                      ? "Loading..."
                      : `${attendance.presentCount} / ${attendance.totalStudents}`
                  }
                  icon={FaUserGraduate}
                  iconBg="bg-green-500"
                />
                {/* 
                <StatCard
                  title="Staff Present Today"
                  value={getStaffValue()}
                  icon={FaUsers}
                  iconBg="bg-orange-400"
                  onClick={() => navigate("/staff-attendance?preset=staff")}
                /> */}

                <StatCard
                  title="Conductors Present"
                  value={getRoleValue("conductor")}
                  icon={FaBus}
                  iconBg="bg-teal-500"
                  onClick={() => navigate("/staff-attendance?preset=conductor")}
                />
              </div>
            </main>

            <Charts analytics={accountsStats} loading={accountsLoading} />
          </>
        );
    }
  };

  return (
    <div className="flex">
      <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

      <main className="flex-1 p-2">
        <h1 className="text-2xl font-semibold mb-2">{activeMenu}</h1>
        {renderContent()}
      </main>
    </div>
  );
}
